// M&A Phase 1 deal lens — the "what happens if we buy X?" composition.
//
// Charter: docs/charters/ma-pmi-product-charter.md §1 Phase 1. Property ledger:
// docs/research/ma-golden-worksheet-20260709.md, executable at
// packages/lib/src/ma-golden-worksheet.spec.ts. Both are law here.
//
// This module authors ZERO dollars. Every figure in `MaDealLensDto` is either
// copied from an input (carrying that input's certainty class and source ids)
// or derived by an arithmetic named in `derivedFrom`. Net is always
// gross - costs-to-achieve, never a free-standing number. C1 committed and C2
// modelled dollars are separate rows and separate tiles: a combined gross
// exists only as a receipted composition whose parts are BOTH rendered
// alongside it (`grossByCertaintyClass`), never as a lone certainty tile.
//
// Seat counts re-derive from `buildMaFusionDemoDryRun`'s overlap families; no
// seat number in this DTO is typed as text.

import {
  buildMaFusionDemoDryRun,
  type MaFusionDemoDryRun,
  type MaTargetPack,
  type MaTargetPackField,
} from './ma-target-packs';
import {
  buildSynergyCandidateRegister,
  type SynergyCandidateMapEntry,
} from './ma-synergy-candidate-map';
import {
  type SynergyCertaintyClass,
  type SynergyLeverCommitment,
} from './ma-synergy-commitment';
import { selectMaDealLensSynergyEntries } from './ma-deal-lens-dedupe';
import { buildMaDealLensGaps } from './ma-deal-lens.gaps';
import {
  CERTAINTY_STRENGTH_RANK,
  EVIDENCE_GRADE_MAX_RANK,
  GROSS_ROW_BY_CERTAINTY_CLASS,
  MA_DEAL_LENS_CASE_KEYS,
  MA_DEAL_LENS_DEAL_FACT_KEYS,
  MA_DEAL_LENS_SCHEMA_VERSION,
  MA_DEAL_LENS_SEAT_BASIS,
  type BuildMaDealLensInput,
  type MaDealLensCase,
  type MaDealLensCaseKey,
  type MaDealLensCaseLever,
  type MaDealLensCases,
  type MaDealLensCandidate,
  type MaDealLensCertaintyClass,
  type MaDealLensDealFactInput,
  type MaDealLensDto,
  type MaDealLensEvBridgeKey,
  type MaDealLensEvBridgeRow,
  type MaDealLensGrossByCertaintyClass,
  type MaDealLensOverlapZone,
  type MaDealLensPackFactKey,
  type MaDealLensTargetFact,
  type MaDealLensUnit,
} from './ma-deal-lens.types';

export * from './ma-deal-lens.types';


export function buildMaDealLens(input: BuildMaDealLensInput): MaDealLensDto {
  const dryRun = buildMaFusionDemoDryRun({
    targetPack: input.targetPack,
    dealThesis: input.dealThesis,
    acquirer: input.acquirer,
  });
  const register = buildSynergyCandidateRegister({
    dealId: input.dealId,
    asOf: input.asOf,
    commitments: input.commitments,
    targetPack: input.targetPack,
  });
  const commitmentsById = new Map(input.commitments.map((c) => [c.synergyLeverId, c]));
  const pricing = selectMaDealLensSynergyEntries(
    register.entries.filter(
      (entry) => entry.countsTowardRegisterTotals && commitmentsById.has(entry.synergyLeverId)
    ),
    input.commitments
  );

  const targetFacts = buildTargetFacts(input.targetPack, input.dealFacts);
  const cases = buildCases(pricing.entries);
  const standaloneOi = input.targetPack.financials.operatingIncomeUsd;
  const evBridge = buildEvBridge(standaloneOi, cases.base);
  const candidates = buildCandidates(register.entries, commitmentsById);

  const dto: MaDealLensDto = {
    schemaVersion: MA_DEAL_LENS_SCHEMA_VERSION,
    verdict: buildVerdict(input.targetPack, targetFacts, cases.base, evBridge),
    targetFacts,
    evBridge,
    cases,
    candidates,
    overlapZones: buildOverlapZones(dryRun),
    gaps: buildMaDealLensGaps(input, register, standaloneOi, pricing.separationPendingLeverIds),
    provenance: {
      packId: input.targetPack.packId,
      dealId: input.dealId,
      sourceIds: dedupeSorted([
        ...input.targetPack.sources.map((source) => source.sourceId),
        ...input.dealFacts.flatMap((fact) => fact.sourceIds),
      ]),
      asOf: input.asOf,
    },
  };

  assertMaDealLensInvariants(dto);
  return dto;
}

/**
 * The golden-worksheet property ledger, enforced at build time. A composition
 * that trips any of these must not reach a board surface.
 */
export function assertMaDealLensInvariants(dto: MaDealLensDto): void {
  for (const caseKey of MA_DEAL_LENS_CASE_KEYS) {
    const dealCase = dto.cases[caseKey];
    assertEquals(
      dealCase.netUsd,
      roundCurrency(dealCase.grossUsd - dealCase.oneTimeCostUsd),
      `${caseKey} net must equal gross - costs-to-achieve`
    );
    assertEquals(
      roundCurrency(sumBy(dealCase.grossByCertaintyClass, (part) => part.grossUsd)),
      dealCase.grossUsd,
      `${caseKey} gross must equal the sum of its certainty-class parts`
    );
    for (const lever of dealCase.byLever) {
      assertEquals(
        lever.netUsd,
        roundCurrency(lever.grossUsd - lever.oneTimeCostUsd),
        `${caseKey} lever ${lever.synergyLeverId} net must equal gross - cost`
      );
    }
  }

  const rowsByKey = new Map(dto.evBridge.map((row) => [row.key, row]));
  for (const row of dto.evBridge) {
    for (const key of row.derivedFrom) {
      if (!rowsByKey.has(key)) {
        throw new Error(`EV bridge row ${row.key} derives from absent row ${key}`);
      }
    }
  }

  // A combined gross may never render without every certainty-class part visible.
  for (const part of dto.cases.base.grossByCertaintyClass) {
    const grossKey = GROSS_ROW_BY_CERTAINTY_CLASS[part.certaintyClass];
    if (!grossKey || !rowsByKey.has(grossKey)) {
      throw new Error(
        `base case prices ${part.certaintyClass} with no matching EV-bridge gross row`
      );
    }
  }

  assertNetBridgeRow(rowsByKey, dto.cases.base);
}

function assertNetBridgeRow(
  rowsByKey: ReadonlyMap<MaDealLensEvBridgeKey, MaDealLensEvBridgeRow>,
  baseCase: MaDealLensCase
): void {
  const net = rowsByKey.get('net_synergy_base');
  if (!net || net.valueUsd === null) throw new Error('EV bridge must price net_synergy_base');
  assertEquals(net.valueUsd, baseCase.netUsd, 'EV bridge net must equal the base case net');

  const grossFromParts = sumBy(net.derivedFrom, (key) => {
    const row = rowsByKey.get(key);
    return key === 'costs_to_achieve_one_time' ? 0 : (row?.valueUsd ?? 0);
  });
  assertEquals(
    net.valueUsd,
    roundCurrency(grossFromParts - baseCase.oneTimeCostUsd),
    'EV bridge net must re-derive from its named gross rows minus costs-to-achieve'
  );

  const combined = rowsByKey.get('combined_operating_income_delta');
  const standalone = rowsByKey.get('target_standalone_operating_income');
  const standaloneUsd = standalone?.valueUsd ?? null;
  if (!combined || combined.valueUsd === null || standaloneUsd === null) return;
  assertEquals(
    combined.valueUsd,
    roundCurrency(standaloneUsd + net.valueUsd),
    'combined operating-income delta must equal standalone operating income + net synergy'
  );
}

function buildTargetFacts(
  pack: MaTargetPack,
  dealFacts: readonly MaDealLensDealFactInput[]
): readonly MaDealLensTargetFact[] {
  const packFacts: readonly MaDealLensTargetFact[] = [
    packFact('legal_name', 'Legal name', pack.identity.legalName, 'text'),
    packFact('industry', 'Industry', pack.identity.industry, 'text'),
    packFact('headquarters', 'Headquarters', pack.identity.headquarters, 'text'),
    packFact('fiscal_period', 'Fiscal period', pack.financials.fiscalPeriod, 'text'),
    packFact('revenue_usd', 'Revenue', pack.financials.revenueUsd, 'usd'),
    packFact(
      'operating_income_usd',
      'Standalone operating income',
      pack.financials.operatingIncomeUsd,
      'usd'
    ),
    packFact('sga_usd', 'SG&A anchor', pack.financials.sgaUsd, 'usd'),
    packFact('total_headcount', 'Headcount', pack.workforce.totalHeadcount, 'headcount'),
  ];

  const supplied = new Map(dealFacts.map((fact) => [fact.key, fact]));
  const dealRows = MA_DEAL_LENS_DEAL_FACT_KEYS.flatMap((key) => {
    const fact = supplied.get(key);
    if (!fact) return [];
    return [
      {
        key: fact.key,
        label: fact.label,
        value: fact.value,
        unit: fact.unit,
        certaintyClass: fact.certaintyClass,
        sourceIds: fact.sourceIds,
        notes: fact.notes ?? null,
      } satisfies MaDealLensTargetFact,
    ];
  });

  return [...packFacts, ...dealRows];
}

function packFact(
  key: MaDealLensPackFactKey,
  label: string,
  field: MaTargetPackField<string | number | null>,
  unit: MaDealLensUnit
): MaDealLensTargetFact {
  return {
    key,
    label,
    value: field.value,
    unit,
    certaintyClass: field.certaintyClass,
    sourceIds: field.sourceRefs,
    notes: field.notes ?? null,
  };
}

function buildCases(counting: readonly SynergyCandidateMapEntry[]): MaDealLensCases {
  return {
    conservative: buildCase('conservative', counting),
    base: buildCase('base', counting),
    stretch: buildCase('stretch', counting),
  };
}

function buildCase(
  caseKey: MaDealLensCaseKey,
  counting: readonly SynergyCandidateMapEntry[]
): MaDealLensCase {
  const byLever = counting.map((entry) => {
    const grossUsd = caseGrossFor(entry, caseKey);
    return {
      synergyLeverId: entry.synergyLeverId,
      certaintyClass: entry.certaintyClass,
      grossUsd,
      oneTimeCostUsd: entry.bands.oneTimeCostUsd,
      netUsd: roundCurrency(grossUsd - entry.bands.oneTimeCostUsd),
    } satisfies MaDealLensCaseLever;
  });

  const grossUsd = roundCurrency(sumBy(byLever, (lever) => lever.grossUsd));
  const oneTimeCostUsd = roundCurrency(sumBy(byLever, (lever) => lever.oneTimeCostUsd));

  return {
    caseKey,
    grossUsd,
    oneTimeCostUsd,
    netUsd: roundCurrency(grossUsd - oneTimeCostUsd),
    grossByCertaintyClass: groupGrossByCertaintyClass(byLever),
    byLever,
  };
}

function caseGrossFor(entry: SynergyCandidateMapEntry, caseKey: MaDealLensCaseKey): number {
  const byCase: Readonly<Record<MaDealLensCaseKey, number>> = {
    conservative: entry.bands.conservativeRunRateUsd,
    base: entry.bands.baseRunRateUsd,
    stretch: entry.bands.stretchRunRateUsd,
  };
  return byCase[caseKey];
}

function groupGrossByCertaintyClass(
  byLever: readonly MaDealLensCaseLever[]
): readonly MaDealLensGrossByCertaintyClass[] {
  const grouped = new Map<SynergyCertaintyClass, { grossUsd: number; leverCount: number }>();
  for (const lever of byLever) {
    const current = grouped.get(lever.certaintyClass) ?? { grossUsd: 0, leverCount: 0 };
    grouped.set(lever.certaintyClass, {
      grossUsd: current.grossUsd + lever.grossUsd,
      leverCount: current.leverCount + 1,
    });
  }
  return [...grouped.entries()]
    .map(([certaintyClass, totals]) => ({
      certaintyClass,
      grossUsd: roundCurrency(totals.grossUsd),
      leverCount: totals.leverCount,
    }))
    .sort(
      (left, right) =>
        CERTAINTY_STRENGTH_RANK[left.certaintyClass] -
        CERTAINTY_STRENGTH_RANK[right.certaintyClass]
    );
}

function buildEvBridge(
  standaloneOi: MaTargetPackField<number | null>,
  baseCase: MaDealLensCase
): readonly MaDealLensEvBridgeRow[] {
  const grossRows = baseCase.grossByCertaintyClass.flatMap((part) => {
    const key = GROSS_ROW_BY_CERTAINTY_CLASS[part.certaintyClass];
    if (!key) return [];
    return [
      {
        key,
        label: `Gross synergy run-rate (base, ${part.certaintyClass})`,
        valueUsd: part.grossUsd,
        certaintyClass: part.certaintyClass,
        derivedFrom: [],
        sourceIds: [],
      } satisfies MaDealLensEvBridgeRow,
    ];
  });

  const grossKeys = grossRows.map((row) => row.key);
  const netCertainty = weakestDealLensCertaintyClass(
    baseCase.grossByCertaintyClass.map((part) => part.certaintyClass)
  );
  const netRow: MaDealLensEvBridgeRow = {
    key: 'net_synergy_base',
    label: 'Net synergy run-rate (base)',
    valueUsd: baseCase.netUsd,
    certaintyClass: netCertainty,
    derivedFrom: [...grossKeys, 'costs_to_achieve_one_time'],
    sourceIds: [],
  };

  const standaloneRow: MaDealLensEvBridgeRow = {
    key: 'target_standalone_operating_income',
    label: 'Target standalone operating income',
    valueUsd: standaloneOi.value,
    certaintyClass: standaloneOi.certaintyClass,
    derivedFrom: [],
    sourceIds: standaloneOi.sourceRefs,
  };

  return [
    standaloneRow,
    ...grossRows,
    {
      key: 'costs_to_achieve_one_time',
      label: 'Costs to achieve (one-time)',
      valueUsd: baseCase.oneTimeCostUsd,
      certaintyClass: netCertainty,
      derivedFrom: [],
      sourceIds: [],
    },
    netRow,
    {
      key: 'combined_operating_income_delta',
      label: 'Combined operating-income delta',
      valueUsd:
        standaloneOi.value === null
          ? null
          : roundCurrency(standaloneOi.value + baseCase.netUsd),
      certaintyClass: weakestDealLensCertaintyClass([
        standaloneOi.certaintyClass,
        netCertainty,
      ]),
      derivedFrom: ['target_standalone_operating_income', 'net_synergy_base'],
      sourceIds: [],
    },
  ];
}

function buildCandidates(
  entries: readonly SynergyCandidateMapEntry[],
  commitmentsById: ReadonlyMap<string, SynergyLeverCommitment>
): readonly MaDealLensCandidate[] {
  return entries
    .filter((entry) => entry.certaintyClass !== 'blocked')
    .map((entry) => ({
      entry,
      commitment: commitmentsById.get(entry.synergyLeverId) ?? null,
    }))
    .sort((left, right) => {
      const bandDelta = right.entry.bands.baseRunRateUsd - left.entry.bands.baseRunRateUsd;
      if (bandDelta !== 0) return bandDelta;
      const classDelta =
        CERTAINTY_STRENGTH_RANK[left.entry.certaintyClass] -
        CERTAINTY_STRENGTH_RANK[right.entry.certaintyClass];
      if (classDelta !== 0) return classDelta;
      return left.entry.synergyLeverId.localeCompare(right.entry.synergyLeverId);
    })
    .map(({ entry, commitment }, index) => ({
      rank: index + 1,
      registerRecommendationRank: entry.recommendationRank,
      synergyLeverId: entry.synergyLeverId,
      title: entry.title,
      certaintyClass: entry.certaintyClass,
      valueType: entry.valueType,
      ownerLabel: entry.ownerLabel,
      workstreamId: entry.workstreamId,
      conservativeRunRateUsd: entry.bands.conservativeRunRateUsd,
      baseRunRateUsd: entry.bands.baseRunRateUsd,
      stretchRunRateUsd: entry.bands.stretchRunRateUsd,
      oneTimeCostUsd: entry.bands.oneTimeCostUsd,
      baseNetRunRateUsd: entry.baseNetRunRateUsd,
      blockingGapCodes: entry.promotionGapCodes,
      footprintAtoms:
        commitment && commitment.poolFootprint.footprintStatus === 'attributed'
          ? commitment.poolFootprint.atoms
          : [],
      countsTowardCases: entry.countsTowardRegisterTotals && commitment !== null,
    }));
}

function buildOverlapZones(dryRun: MaFusionDemoDryRun): readonly MaDealLensOverlapZone[] {
  const byZone = new Map<
    string,
    { acquirer: Set<string>; target: Set<string>; classes: MaDealLensCertaintyClass[] }
  >();

  for (const family of dryRun.overlapSequence.sharedMemberFamilies) {
    const bucket = byZone.get(family.label) ?? {
      acquirer: new Set<string>(),
      target: new Set<string>(),
      classes: [],
    };
    for (const member of family.members) {
      const seats = member.orgSide === 'PRIMARY' ? bucket.acquirer : bucket.target;
      seats.add(member.positionId);
    }
    bucket.classes.push(family.certaintyClass);
    byZone.set(family.label, bucket);
  }

  return [...byZone.entries()]
    .map(([zone, bucket]) => ({
      zone,
      acquirerSeats: bucket.acquirer.size,
      targetSeats: bucket.target.size,
      overlapSeats: Math.min(bucket.acquirer.size, bucket.target.size),
      sourceClass: weakestDealLensCertaintyClass(bucket.classes),
      seatBasis: MA_DEAL_LENS_SEAT_BASIS,
    }))
    .sort(
      (left, right) => right.overlapSeats - left.overlapSeats || left.zone.localeCompare(right.zone)
    );
}

function buildVerdict(
  pack: MaTargetPack,
  targetFacts: readonly MaDealLensTargetFact[],
  baseCase: MaDealLensCase,
  evBridge: readonly MaDealLensEvBridgeRow[]
): string {
  // An undisclosed fact (`value: null`) never counts as evidence, no matter how strong
  // its label — a public_filing/ir_material field the pack could not source is an
  // unknown, not a filing-grade fact, and claiming it as evidence overstates coverage.
  const evidenceFacts = targetFacts.filter(
    (fact) =>
      fact.value !== null && CERTAINTY_STRENGTH_RANK[fact.certaintyClass] <= EVIDENCE_GRADE_MAX_RANK
  ).length;
  const committed = baseCase.grossByCertaintyClass.find(
    (part) => part.certaintyClass === 'c1_committed_lever'
  );
  const modelled = baseCase.grossByCertaintyClass.find(
    (part) => part.certaintyClass === 'c2_modelled_upside'
  );
  const standalone = evBridge.find((row) => row.key === 'target_standalone_operating_income');

  return (
    `Buying ${pack.identity.legalName.value} underwrites to ${usd(baseCase.netUsd)} of base ` +
    `net synergy run-rate against ${usd(standalone?.valueUsd ?? null)} of target standalone ` +
    `operating income, split ${usd(committed?.grossUsd ?? 0)} gross across ` +
    `${committed?.leverCount ?? 0} committed lever(s) and ${usd(modelled?.grossUsd ?? 0)} ` +
    `across ${modelled?.leverCount ?? 0} modelled lever(s) — never summed into one certainty ` +
    `class — on ${evidenceFacts} of ${targetFacts.length} target facts at filing or IR grade ` +
    `and ${targetFacts.length - evidenceFacts} extrapolated.`
  );
}

export function weakestDealLensCertaintyClass(
  classes: readonly MaDealLensCertaintyClass[]
): MaDealLensCertaintyClass {
  return classes.reduce<MaDealLensCertaintyClass>(
    (weakest, candidate) =>
      CERTAINTY_STRENGTH_RANK[candidate] > CERTAINTY_STRENGTH_RANK[weakest] ? candidate : weakest,
    'c0_observed'
  );
}

function usd(value: number | null): string {
  if (value === null) return 'an undisclosed amount';
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

function sumBy<T>(items: readonly T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}

function dedupeSorted(values: readonly string[]): readonly string[] {
  return [...new Set(values)].sort();
}

function assertEquals(actual: number, expected: number, message: string): void {
  if (Math.abs(actual - expected) > 0.01) {
    throw new Error(`${message} (got ${actual}, expected ${expected})`);
  }
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}
